import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useCart } from '../../context/CartContext';
import Container from '../ui/Container';
import AnnouncementBar from './AnnouncementBar';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { getCartCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop Kits', path: '/shop' },
    { name: 'Bundles', path: '/shop?category=bundles' },
    { name: 'Parent Guide', path: '/guide' },
    { name: 'Support', path: '/faq' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {!scrolled && <AnnouncementBar />}
      <nav className={`bg-white transition-all duration-300 ${scrolled ? 'py-3 shadow-xl border-b-2 border-ink-navy' : 'py-5 border-b-4 border-ink-navy'}`}>
        <Container>
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-sky-blue rounded-xl border-2 border-ink-navy flex items-center justify-center group-hover:rotate-12 transition-all shadow-[3px_3px_0_0_#1e1b4b]">
                <SafeIcon name="Hexagon" className="text-white text-2xl" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-extrabold text-2xl text-ink-navy tracking-tight">
                  LittleLab <span className="text-sky-blue">STEM</span>
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Nurturing Curiosity</span>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-bold text-sm uppercase tracking-wide transition-all relative group ${
                    isActive(link.path) ? 'text-sky-blue' : 'text-ink-navy hover:text-sky-blue'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-1 bg-lemon-yellow transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                </Link>
              ))}
              
              <button className="flex items-center gap-2 p-2.5 bg-slate-50 border-2 border-ink-navy rounded-xl hover:bg-lemon-yellow transition-all relative group shadow-[2px_2px_0_0_#1e1b4b]">
                <SafeIcon name="ShoppingCart" className="text-xl text-ink-navy" />
                <span className="font-bold text-sm hidden lg:block">Cart</span>
                {getCartCount() > 0 && (
                  <span className="absolute -top-3 -right-3 bg-red-500 text-white text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border-2 border-ink-navy shadow-sm animate-bounce">
                    {getCartCount()}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <div className="md:hidden flex items-center gap-4">
               <button className="relative p-2">
                <SafeIcon name="ShoppingCart" className="text-2xl text-ink-navy" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                    {getCartCount()}
                  </span>
                )}
              </button>
              <button onClick={() => setIsOpen(!isOpen)} className="text-ink-navy p-1">
                <SafeIcon name={isOpen ? "X" : "Menu"} className="text-3xl" />
              </button>
            </div>
          </div>
        </Container>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b-4 border-ink-navy shadow-2xl animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-lg font-bold border-2 transition-all ${
                    isActive(link.path) ? 'bg-sky-light border-ink-navy text-ink-navy' : 'border-transparent text-ink-navy'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Navbar;